import React, { useState, useEffect, useRef, useMemo } from "react";
import clsx from "clsx";
import { Dialog, DialogActions, DialogContent, Divider, Grid } from "@mui/material";
import { Box, Button, Form, RadioField, Typography, Stack, Alert } from "@curves/ui-base";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { isEmpty, isEqual, isFunction } from "@util/utils";
import useOptions from "@util/hook/useOptions";
import { ReadTextField } from "@icoach/components";
import { useMemberPaymentApi } from "@apis/useMemberPaymentApi";
import CreditCardEditor from "@icoach/components/creditCard/creditCardEditor/CreditCardEditor";
import BankAccountEditor from "@icoach/components/bankAccount/bankAccountEditor/BankAccountEditor";
import { PAYMENT_SOURCE_TYPE_MAP, PAYMENT_TYPE } from "@icoach/constants";

const paymentComponentMap = {
	[PAYMENT_TYPE.CREDITCARD]: CreditCardEditor,
	[PAYMENT_TYPE.ACH]: BankAccountEditor,
};

const componentPropMap = {
	[PAYMENT_TYPE.CREDITCARD]: {
		showCardRelation: true,
		showBankName: true,
		required: true,
	},
	[PAYMENT_TYPE.ACH]: {},
};

const defaultPaymentDataMap = {
	[PAYMENT_TYPE.CREDITCARD]: raw => ({
		memberPaymentID: raw?.memberPaymentID || 0,
		sourceType: raw?.sourceType || 0,
		memberID: raw?.memberID || 0,
		creditCardBankName: raw?.creditCardBankName || "",
		creditCardType: raw?.creditCardType || 1, // 1: VISA, 2: MasterCard, 3: JCB
		creditCardNo: raw?.creditCardNo || "",
		cardExpiryDate: raw?.cardExpiryDate || "",
		cardName: raw?.cardName || "",
		cardRelation: raw?.cardRelation || "",
		isMembershipCurrent: raw.isCurrent && isEqual(raw.sourceType, 1),
	}),
	[PAYMENT_TYPE.ACH]: raw => ({
		memberPaymentID: raw?.memberPaymentID || 0,
		memberID: raw?.memberID || 0,
		bankCode: raw?.bankCode || "",
		bankName: raw?.bankName || "",
		bankAccount: raw?.bankAccount || "",
		bankAccountName: raw?.bankAccountName || "",
		bankAccountID: raw?.bankAccountID || 0,
	}),
};

const getDefaultPaymentData = (paymentType, rawData = {}) => {
	const getDefaultData = defaultPaymentDataMap[paymentType];
	return isFunction(getDefaultData) ? getDefaultData(rawData) : {};
};

const EditPaymentInfoDialog = React.forwardRef(({ open, sourceData, refresh, onClose }, ref) => {
	if (!open) return null;

	const { title, memberID, memberName, memberNo, sourceType } = sourceData || {};

	// 取得 options
	const { getMemberPaymentOptionsApi } = useMemberPaymentApi();
	const { options, getOptionsApi } = useOptions({
		getOptionsApi: () => getMemberPaymentOptionsApi(memberID),
		isFetchOnMount: false,
	});

	// 設定付款方式
	const [paymentType, setPaymentType] = useState(sourceData.paymentType || PAYMENT_TYPE.CREDITCARD);

	// 指定付款方式的 component
	const Component = paymentComponentMap[paymentType];
	const componentRef = useRef(null);

	const handleSubmit = () => {
		const { onSubmit } = componentRef.current || {};
		isFunction(onSubmit) && onSubmit();
	};

	const handleSuccess = () => {
		isFunction(refresh) && refresh();
		onClose();
	};

	const defaultPaymentData = useMemo(() => getDefaultPaymentData(paymentType, sourceData), [paymentType, sourceData]);

	useEffect(
		() => {
			if (open) {
				getOptionsApi();
			} else {
				setPaymentType(PAYMENT_TYPE.CREDITCARD);
			}
		},
		// eslint-disable-next-line
		[open],
	);

	return (
		<Dialog className={clsx("simple-content", "dialog", "payment-dialog")} PaperProps={{ className: "wx-70rem", component: Form }} onSubmit={handleSubmit} open={open} fullWidth>
			<GradientColorHeader onClose={onClose}>修改付款資訊</GradientColorHeader>
			<DialogContent>
				<Alert className={"mb-1"} color="info" severity="info" variant="filled">
					為保障會員權益，每筆付款資料皆為獨立授權。本次變更僅適用於此筆付款設定，不會同步其他付款項目，請視需要分別進行變更與授權。
				</Alert>
				<Typography className={"font-weight-bold"} variant={"h6"}>
					{title}
				</Typography>
				<Grid spacing={2} container>
					<Grid xs={6} item>
						<ReadTextField label={"會員"}>{memberName}</ReadTextField>
					</Grid>
					<Grid xs={6} item>
						<ReadTextField label={"會員編號"}>{memberNo}</ReadTextField>
					</Grid>
					<Grid xs={12} item>
						<Divider />
					</Grid>
					{isEqual(sourceType, PAYMENT_SOURCE_TYPE_MAP.MEMBERSHIP) && (
						<Grid xs={12} item>
							<Stack direction="row">
								<RadioField value={paymentType} onChange={(_, val) => setPaymentType(val)} row required>
									{!isEmpty(options?.paymentTypeOptions) &&
										options?.paymentTypeOptions.filter(item => item || !item.disabled).map(({ value, text }) => <RadioField.Radio key={value} value={value} label={text} />)}
								</RadioField>
								<Typography variant="body2" className="text-gray payment-card-type-select">
									(
									<Box component="span" className="text-error">
										**
									</Box>
									只能選擇一種付款方式)
								</Typography>
							</Stack>
						</Grid>
					)}

					{Component && (
						<Grid xs={12} item>
							<Component {...componentPropMap[paymentType]} ref={componentRef} sourceData={defaultPaymentData} options={options} refresh={handleSuccess} />
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="outlined">
					取消編輯
				</Button>
				<Button type={"submit"} variant="contained">
					確認送出
				</Button>
			</DialogActions>
		</Dialog>
	);
});

export default EditPaymentInfoDialog;
